// @ts-strict-ignore
import { ComplexArticleBlueprintVersionOverwriteMode } from '@admintool/article/generate-article/complex-article-blueprints/complex-article-blueprint';
import { ArticleBehaviorPropertyDef } from '@article/constants/article-behavior-property-def';
import { ProjectHeadVO, ProjectOptionDefVO, ProjectOptionVO, ProjectSaveInfoVO, ProjectVO } from '@project/model/vo/project.vo';
import { ProjectModel } from '@project/model/project.model';
import { UserModel } from '@user/model/user.model';
import { UserUtils } from '@user/service/user-utils';
import { ProjectOrderingUtils } from '@project/components/project/util/project-ordering-utils';
import { ProjectStatesDef } from '@project/components/project/constants/project-states-def';
import { ProjectOptionUtils } from '../util/project-option.utils';
import { ProjectGroupVO } from '@project/model/vo/project-group.vo';
import { ProjectUtils } from '@project/components/project/util/project-utils';
import { AlertHelper } from '@shared/alert/alert.helper';
import { ProjectService } from '@project/components/import-export/service/project.service';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { PropertyUtils } from '@article/util/property-utils';
import { ArticleUsageVO } from '@article/model/vo/article.vo';
import { InitManager } from '@shared/manager/init.manager';
import { AlertButton } from '@shared/alert/alertButton';
import { CarrierBlockManager } from '@project/components/article/manager/carrier-block.manager';
import { CompositeArticleConfigurationVO } from '@article/model/vo/composite-article-configuration.vo';
import { SaveCompositeArticleService } from '../../../service/save-composite-article.service';
import { SaveProjectCloseType, SaveProjectPopupData } from '../save/save.component';
import { ArrayUtils } from '@shared/util/array-utils';
import { ToastrService } from 'ngx-toastr';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { BuildingBlockHeadVO } from '@project/model/vo/building-block.vo';
import { escape } from 'lodash';
import { EditProjectVersionResult } from '../edit-project-version/edit-project-version.component';
import { ProjectOptionDefIds } from '@project/components/project/constants/project-option-def-ids';
import { Injectable } from '@angular/core';
import { lastValueFrom, Subject } from 'rxjs';
import { ApplicationService } from '@shared/application/application.service';
import { Log } from '@shared/debug/log';
import { OBJRepresentationDataVO, RepresentationDataVO } from '@shared/vo/representation.vo';
import { UserSettings } from '@user/model/user-settings';
import { RepresentationService } from '../../../service/representation.service';
import { ProjectRequestProcessorDelegator } from '../../request/processor/project-request-processor.delegator';
import { ProjectNavigationPresenter } from '@project-navigation/project-navigation.presenter';
import { ProjectDataService } from '../../../service/project-data.service';
import { ProjectComponentPresenter } from '../../project-component.presenter';
import { ProjectUserModel } from '../../../model/project-user.model';
import { SaveCaeProjectService } from './save-cae-project.service';
import { StringUtils } from '@shared/util/string-utils';
import { assertExhaustiveSwitch } from '@shared/util/ts-utils';

export enum SaveAsCompositeMode {
  NONE = 0,
  ARTICLE = 1,
  BUILDING_BLOCK = 2
}

export interface SavedProjectData {
  success: boolean;
  projectVO?: ProjectVO;
}

export type SaveType = 'current' | 'asNewVersion' | 'keepModificationDate' | 'overwriteVersion';

@Injectable({ providedIn: 'root' })
export class SaveProjectService {
  public static readonly PREPARE_PROJECT_SAVE: string = 'prepareProjectSave';
  public static readonly PREPARE_PROJECT_SAVE_AS: string = 'prepareProjectSaveAs';

  private _showOverwriteAlerts: boolean = true;

  private _createSubprojects: boolean = false;
  private configurationList: ProjectHeadVO[] = [];

  private newConfigName: string = '';
  private saveAsCompositeMode: SaveAsCompositeMode = SaveAsCompositeMode.NONE;
  private isPinned: boolean = false;

  public confirmSaveType: number = -1;

  private selectedProjectGroup: ProjectGroupVO;

  private _baseArticleIdInputValue: number;
  private compositeArticleConfiguration: CompositeArticleConfigurationVO = null;

  private _prepareSave: boolean = false;

  private _savedProject$ = new Subject<SavedProjectData>();

  constructor(
    private userModel: UserModel,
    private userSettings: UserSettings,
    private projectModel: ProjectModel,
    private representationService: RepresentationService,
    private alertHelper: AlertHelper,
    private projectService: ProjectService,
    private projectDataService: ProjectDataService,
    private carrierBlockManager: CarrierBlockManager,
    private saveCompositeArticleService: SaveCompositeArticleService,
    private toastrService: ToastrService,
    private localeResourceManager: LocaleResourceManager,
    private projectRequestProcessorDelegator: ProjectRequestProcessorDelegator,
    private applicationService: ApplicationService,
    private projectNavigationPresenter: ProjectNavigationPresenter,
    private projectComponentPresenter: ProjectComponentPresenter,
    private projectUserModel: ProjectUserModel,
    private readonly saveCaeProjectService: SaveCaeProjectService,
  ) { }

  public get savedProject$() {
    return this._savedProject$.asObservable();
  }

  public async saveCurrentProject(): Promise<void> {
    if (this.isSaveCurrentProjectEnabled()) {
      if (!this.projectModel.currentProject.id && this.projectModel.currentProject.caeType != null) {
        await this.saveCaeProjectService.saveCurrentProject();
        this.projectModel.resetProjectChanged();
      } else if (ProjectUtils.isEmptyProject(this.projectModel.currentProject) || this.projectModel.isNewConfiguration) {
        await this.saveCurrentProjectAs();
      } else {
        await this.prepareSaveProject(SaveProjectService.PREPARE_PROJECT_SAVE);
      }
    }
  }

  public isSaveCurrentProjectEnabled(): boolean {
    if (ProjectUtils.isEmptyProject(this.projectModel.currentProject) || this.projectModel.isNewConfiguration) {
      return true;
    } else {
      return this.projectModel.isCurrentProjectChanged;
    }
  }

  public async saveCurrentProjectAs(): Promise<void> {
    if (!this.projectModel.currentProject.id && this.projectModel.currentProject.caeType != null) {
      return await this.saveCaeProjectService.saveCurrentProject();
    }
    await this.prepareSaveProject();
  }

  public async save(newConfigName: string, selectedProjectGroup: ProjectGroupVO, configurationList: ProjectHeadVO[] = [],
    saveAsCompositeMode: SaveAsCompositeMode = SaveAsCompositeMode.NONE, createSubprojects: boolean = false, baseArticleIdInputValue: number = NaN,
    compositeArticleConfiguration: CompositeArticleConfigurationVO = null,
    showOverwriteAlerts: boolean = true, isPinned: boolean = false): Promise<void> {
    this.newConfigName = newConfigName;
    this.selectedProjectGroup = selectedProjectGroup;
    this.saveAsCompositeMode = saveAsCompositeMode;
    this._baseArticleIdInputValue = baseArticleIdInputValue;
    this.compositeArticleConfiguration = compositeArticleConfiguration;
    this.configurationList = configurationList;
    this._showOverwriteAlerts = showOverwriteAlerts;
    this.isPinned = isPinned;
    const configName: string = this.newConfigName;
    const currentProject: ProjectVO = this.projectModel.currentProject;
    const existingProject: ProjectHeadVO = this.findExistingConfiguration(configName, currentProject.id);
    this._createSubprojects = createSubprojects;
    if (!existingProject || saveAsCompositeMode) {
      await this.checkSave(existingProject, saveAsCompositeMode, createSubprojects, null, this.getAllowOverwrite(compositeArticleConfiguration));
    } else {
      try {
        const projectSaveInfo = await this.projectDataService.loadProjectSaveInfo(existingProject.id).toPromise();
        await this.checkSave(projectSaveInfo.projectWithHighestVersion, this.saveAsCompositeMode, this._createSubprojects, projectSaveInfo);
      } catch (error) {
        Log.error('Could not load project save info: ', error)
        await this.saveFailure();
      }
    }
  }

  private getAllowOverwrite(compositeArticleConfiguration: CompositeArticleConfigurationVO) {
    if(!compositeArticleConfiguration) return false;
    const versionOverwriteMode = PropertyUtils.getStringPropertyValue(compositeArticleConfiguration.behaviorProperties, ArticleBehaviorPropertyDef.VERSION_OVERWRITE_MODE) as ComplexArticleBlueprintVersionOverwriteMode;
    return versionOverwriteMode == 'overwrite-latest-version' || versionOverwriteMode == 'new-version';
  }

  private async checkSave(existingProject: ProjectHeadVO, saveAsCompositeMode: SaveAsCompositeMode, createSubprojects: boolean,
    projectSaveInfo: ProjectSaveInfoVO, allowOverwriteBuildingBlocks: boolean = false): Promise<void> {
    const configName: string = this.newConfigName;
    const currentProject: ProjectVO = this.projectModel.currentProject;
    if (!saveAsCompositeMode && existingProject && existingProject.isSharedReadOnly) {
      this.showAlert.error.readOnly();
      return;
    }
    if (!saveAsCompositeMode && existingProject && ProjectOrderingUtils.isWriteProtected(existingProject, this.userModel.user)
      && !ProjectOrderingUtils.canCreateNewVersion(existingProject, this.userModel.user)) {
      this.showAlert.error.writeProtected();
      return;
    }
    if (!saveAsCompositeMode && existingProject && existingProject.id !== currentProject.id
      && ProjectOrderingUtils.isVersionLocked(existingProject, this.userModel.user)) {
      this.showAlert.error.versionLocked();
      return;
    }

    if (saveAsCompositeMode === SaveAsCompositeMode.BUILDING_BLOCK && !existingProject) {
      try {
        const item: BuildingBlockHeadVO = await this.projectDataService.getBuildingBlockHeadByName(configName).toPromise();
        if (item && !allowOverwriteBuildingBlocks) {
          this.showAlert.error.buildingBlockNameExists(item.name);
          this.alertHelper.showError('save.title.error_building_block_name_exists',
          'save.message.error_building_block_name_exists', {translationParams: [item.name]});
          this.clear();
        } else {
          await this.continueCheckSave(configName, saveAsCompositeMode, createSubprojects, projectSaveInfo);
        }
      } catch (error) {
        Log.error('Could not get building block head by name: ', error);
      }
      return;
    }

    await this.continueCheckSave(configName, saveAsCompositeMode, createSubprojects, projectSaveInfo);
  }

  private async continueCheckSave(configName: string, saveAsCompositeMode: SaveAsCompositeMode, createSubprojects: boolean,
    projectSaveInfo: ProjectSaveInfoVO): Promise<void> {
    if (createSubprojects && this.isSubprojectsWriteProtected(projectSaveInfo)) {
      return;
    }

    const isAllFieldsFilled: boolean = this.isProjectMetadataFilled();
    if (UserUtils.isWagoUser(this.userModel.user) || saveAsCompositeMode || isAllFieldsFilled) {
      await this.continueSaving(saveAsCompositeMode, createSubprojects, projectSaveInfo);
    } else {
      try {
        const success = await this.projectComponentPresenter.openEditConfigurationSettingsComponent({
          projectName: configName,
          projectNameEditable: false,
          projectOptionsEditable: true,
          saveAndFinalizeProject: false,
          allowEditOfOptionLabels: false
        });
        if (success) {
          await this.continueSaving(saveAsCompositeMode, createSubprojects, projectSaveInfo);
        }
      } catch (error) {
        Log.error('Could not open openEditConfigurationSettingsComponent: ', error);
      }
    }
  }

  private async continueSaving(saveAsCompositeMode: SaveAsCompositeMode, createSubprojects: boolean, projectSaveInfo: ProjectSaveInfoVO): Promise<void> {
    const configName: string = this.newConfigName;
    const currentProject: ProjectVO = this.projectModel.currentProject;
    const existingProject: ProjectHeadVO = this.findExistingConfiguration(configName, currentProject.id);

    if (!existingProject) {
      this.projectService.prepareProjectToSaveUnderNewName();
      await this.executeSave(projectSaveInfo, configName, -1, this.selectedProjectGroup, saveAsCompositeMode, createSubprojects);
      return;
    }

    if (saveAsCompositeMode || existingProject.id === currentProject.id) {
      const saveAsNewVersionAvailable = !saveAsCompositeMode && !createSubprojects && ProjectOrderingUtils.canCreateNewVersion(existingProject, this.userModel.user);

      if (saveAsNewVersionAvailable) {
        const data: SaveProjectPopupData = await this.projectComponentPresenter.openProjectSaveRequest(projectSaveInfo, false, false);
        await this.confirmSaveMethodPopupCloseHandler(data);
        return;
      }

      if (await this.userDeclinedOverwrite(configName)) return;
      await this.executeSave(projectSaveInfo, configName, existingProject.id, existingProject.projectGroup, saveAsCompositeMode, createSubprojects);
      return;
    }

    if (await this.userDeclinedOverwrite(configName)) return;

    this.projectService.prepareProjectToSaveUnderNewName();
    await this.executeSave(projectSaveInfo, configName, existingProject.id, existingProject.projectGroup, saveAsCompositeMode, createSubprojects);
  }

  private async userDeclinedOverwrite(configName: string): Promise<boolean> {
    if(!this._showOverwriteAlerts) return false;
    return await this.showAlert.confirm.overwrite(configName);
  }

  public async changeSaveMethod(data: SaveProjectPopupData, newConfigName: string): Promise<void> {
    this.newConfigName = newConfigName;
    await this.confirmSaveMethodPopupCloseHandler(data);
  }

  private async confirmSaveMethodPopupCloseHandler(data: SaveProjectPopupData): Promise<void> {
    const projectSaveInfo: ProjectSaveInfoVO = data.projectSaveInfo;
    let projectVersionDisplayStringWithDate: string = '';
    if (projectSaveInfo) {
      projectVersionDisplayStringWithDate = ProjectUtils.getHighestFinalizedVersionDisplayStringFromProjectSaveInfo(projectSaveInfo, true);
    }
    const existingProject: ProjectHeadVO = !projectSaveInfo ? null : projectSaveInfo.projectWithHighestVersion;
    const saveAsCompositeMode: SaveAsCompositeMode = this.saveAsCompositeMode;
    const configName: string = this.newConfigName;
    if (data.detail <= 0) {
      return;
    }

    this.confirmSaveType = data.detail;
    if (data.detail === SaveProjectCloseType.OVERWRITE_PROJECT) {
      await this.executeSave(projectSaveInfo, configName, this.projectModel.currentProject.id, this.selectedProjectGroup,
        saveAsCompositeMode, false);
    } else if (data.detail === SaveProjectCloseType.SAVE_AS_NEW_VERSION) {
      this.projectModel.currentProject.name = configName;
      if (projectSaveInfo && projectSaveInfo.highestFinalizedProjectVersion > -1) {
        await this.showAlert.info.changesDoNotEffectFinalizedProjectVersion(projectVersionDisplayStringWithDate);
        await this.saveConfiguration(existingProject.id, this.projectModel.currentProject, false,
          InitManager.PROJECT_OPTION_DATA_VERSION, 'asNewVersion');
      } else {
        await this.saveConfiguration(existingProject.id, this.projectModel.currentProject, false,
          InitManager.PROJECT_OPTION_DATA_VERSION, 'asNewVersion');
      }
    } else if (data.detail === SaveProjectCloseType.SAVE_AND_SPLIT) {
      await this.save(this.newConfigName, this.selectedProjectGroup, this.configurationList, this.saveAsCompositeMode,
        this._createSubprojects, this._baseArticleIdInputValue, this.compositeArticleConfiguration, false);
    }
  }

  public async confirmSaveMethodCloseHandler(data: SaveProjectPopupData): Promise<void> {
    const projectSaveInfo: ProjectSaveInfoVO = data.projectSaveInfo;
    let projectVersionDisplayStringWithDate: string = '';
    if (projectSaveInfo) {
      projectVersionDisplayStringWithDate = ProjectUtils.getHighestFinalizedVersionDisplayStringFromProjectSaveInfo(projectSaveInfo, true);
    }
    if (data.detail <= 0) {
      return;
    }
    if (data.detail === SaveProjectCloseType.OVERWRITE_PROJECT) {
      if (projectSaveInfo && projectSaveInfo.highestFinalizedProjectVersion > -1 && projectSaveInfo.projectWithHighestVersion
        && projectSaveInfo.highestFinalizedProjectVersion !== projectSaveInfo.projectWithHighestVersion.currentVersion) {
        await this.showAlert.info.changesDoNotEffectFinalizedProjectVersion(projectVersionDisplayStringWithDate);
        await this.executeSaveProject(this.projectModel.currentProject.id, this.projectModel.currentProject.name,
          this.projectModel.currentProject.projectGroup);
      } else {
        await this.executeSaveProject(this.projectModel.currentProject.id, this.projectModel.currentProject.name,
          this.projectModel.currentProject.projectGroup);
      }
    } else if (data.detail === SaveProjectCloseType.SAVE_AS_NEW_VERSION
      || data.detail === SaveProjectCloseType.SAVE_AND_SPLIT) {
      const createSubprojects: boolean = data.detail === SaveProjectCloseType.SAVE_AND_SPLIT;
      if (projectSaveInfo && projectSaveInfo.highestFinalizedProjectVersion > -1) {
        await this.showAlert.error.changesDoNotEffectFinalizedProjectVersion(projectVersionDisplayStringWithDate);
        this.interceptSaveProjectEvent();
        await this.saveConfiguration(this.projectModel.currentProject.id, this.projectModel.currentProject,
          createSubprojects, InitManager.PROJECT_OPTION_DATA_VERSION, 'asNewVersion');
      } else {
        this.interceptSaveProjectEvent();
        await this.saveConfiguration(this.projectModel.currentProject.id, this.projectModel.currentProject,
          createSubprojects, InitManager.PROJECT_OPTION_DATA_VERSION, 'asNewVersion');
      }
    } else if (data.detail === SaveProjectCloseType.OVERWRITE_ANY_VERSION) {
      this.saveConfiguration(this.projectModel.currentProject.id, this.projectModel.currentProject,
        false, InitManager.PROJECT_OPTION_DATA_VERSION, 'overwriteVersion');

    } else {
      this.confirmSaveType = data.detail;
      this.projectNavigationPresenter.openSaveProjectDialog();
    }
  }

  private isProjectMetadataFilled(): boolean {
    const projectOptionDefs: ProjectOptionDefVO[] = this.projectUserModel.projectOptionDefs;
    const projectOptions: ProjectOptionVO[] = this.projectModel.currentProject.options;

    for (const projectOptionDef of projectOptionDefs) {
      if (!projectOptionDef.isRequired || !projectOptionDef.isVisible) {
        continue;
      }
      const correspondingOption: ProjectOptionVO = ProjectOptionUtils.findProjectOptionByIdentifier(projectOptions,
        projectOptionDef.identifier);
      if (!correspondingOption || !correspondingOption.value) {
        return false;
      }
    }
    return true;
  }

  private findExistingConfiguration(configName: string, currentProjectId: number): ProjectHeadVO {
    let alternativeItem: ProjectHeadVO = null;
    for (const projectHead of this.configurationList) {
      if (projectHead.name === configName) {
        if (projectHead.id === currentProjectId) {
          return projectHead;
        } else {
          alternativeItem = projectHead;
        }
      }
    }
    return alternativeItem;
  }

  private isSubprojectsWriteProtected(projectSaveInfo: ProjectSaveInfoVO): boolean  {
    if (projectSaveInfo && ArrayUtils.isNotEmpty(projectSaveInfo.readOnlySubProjectNames)
      && projectSaveInfo.readOnlySubProjectNames.length > 0) {
      let text: string = '';
      projectSaveInfo.readOnlySubProjectNames.forEach((item) => text += '\n' + item);
      this.showAlert.error.cannotOverwriteProtectedSubproject(text);
      return true;
    }
    return false;
  }

  private async executeSave(projectSaveInfo: ProjectSaveInfoVO, configName: string, existingProjectId: number, parentGroup: ProjectGroupVO,
    saveAsCompositeMode: SaveAsCompositeMode, createSubprojects: boolean = false): Promise<void> {
    let projectVersionDisplayStringWithDate: string = null;
    if (projectSaveInfo) {
      projectVersionDisplayStringWithDate = ProjectUtils.getHighestFinalizedVersionDisplayStringFromProjectSaveInfo(projectSaveInfo, true);
    }
    if ((SaveProjectCloseType.OVERWRITE_PROJECT !== this.confirmSaveType && projectSaveInfo && projectSaveInfo.highestFinalizedProjectVersion > -1)
      || (projectSaveInfo && projectSaveInfo.highestFinalizedProjectVersion > -1 && projectSaveInfo.projectWithHighestVersion
        && projectSaveInfo.highestFinalizedProjectVersion !== projectSaveInfo.projectWithHighestVersion.currentVersion)) {
      await this.showAlert.error.changesDoNotEffectFinalizedProjectVersion(projectVersionDisplayStringWithDate);
      await this.continueExecuteSave(projectSaveInfo, configName, existingProjectId, parentGroup, saveAsCompositeMode, createSubprojects);
    } else {
      await this.continueExecuteSave(projectSaveInfo, configName, existingProjectId, parentGroup, saveAsCompositeMode, createSubprojects);
    }
  }

  private async continueExecuteSave(projectSaveInfo: ProjectSaveInfoVO, configName: string, existingProjectId: number,
    parentGroup: ProjectGroupVO, saveAsCompositeMode: SaveAsCompositeMode, createSubprojects: boolean = false): Promise<void> {
    if (saveAsCompositeMode) {
      const id: number = SaveAsCompositeMode.BUILDING_BLOCK === saveAsCompositeMode ? existingProjectId : this._baseArticleIdInputValue;
      await this.executeSaveProject(id, configName, parentGroup, saveAsCompositeMode,
        this.compositeArticleConfiguration, createSubprojects);
    } else if (SaveProjectCloseType.SAVE_AS_NEW_PROJECT === this.confirmSaveType || existingProjectId === -1 || this.projectModel.isNewConfiguration) {
      const result: EditProjectVersionResult = await this.projectComponentPresenter.openEditProjectVersionComponent(projectSaveInfo, configName, this.configurationList);
      if (result.isSuccess) {
        if (result.newProjectName) {
          configName = result.newProjectName;
        }
        if (result.newProjectId) {
          this.projectService.prepareProjectToSaveUnderNewName();
          existingProjectId = result.newProjectId;
        }
        await this.prepareFinalizeExecuteSave(configName, existingProjectId, parentGroup, createSubprojects, projectSaveInfo);
      }
    } else {
      await this.prepareFinalizeExecuteSave(configName, existingProjectId, parentGroup, createSubprojects, projectSaveInfo);
    }
  }

  private async prepareFinalizeExecuteSave(configName: string, existingProjectId: number, parentGroup: ProjectGroupVO,
    createSubprojects: boolean = false, projectSaveInfo: ProjectSaveInfoVO = null): Promise<void> {
    // it makes more sense to contol showing option about transfer of project, in case new project is really being created by actions
    // in save project view, instead of prior selection in save-method-popup
    if (existingProjectId === -1 && !this.projectModel.isNewConfiguration) {
      const copyProperties = await this.projectComponentPresenter.openCopyProjectPopup(this.projectModel.currentProject);
      if (!copyProperties) {
        return;
      }
      if (copyProperties.copyConfigurationProperties) {
        ProjectOptionUtils.updateLimitationOptionsToDefault(this.projectModel.currentProject, this.projectModel.currentProject.options,
          this.projectUserModel.projectOptionDefs);
      } else {
        // we want to set default options, but keep previously confirmed/edited project version
        // 29105: created by and custom material number should also be kept
        const projectVersionDisplayString: string = ProjectUtils.getVersionDisplayString(this.projectModel.currentProject, false);
        const createdBy: string = ProjectOptionUtils.getValueFromProjectOptions(this.projectModel.currentProject.options, ProjectOptionDefIds.CREATED_BY);
        const customMaterialNumber: string = ProjectOptionUtils.getValueFromProjectOptions(this.projectModel.currentProject.options, ProjectOptionDefIds.CUSTOM_MATERIAL_NUMBER);
        this.projectModel.currentProject.options = [];
        ProjectOptionUtils.setDefaultProjectOptionValues(this.projectModel.currentProject, this.projectUserModel.projectOptionDefs);
        ProjectUtils.setVersionDisplayString(this.projectModel.currentProject, this.projectUserModel.projectOptionDefs, projectVersionDisplayString);
        ProjectUtils.setOrUpdateProjectOption(ProjectOptionDefIds.CREATED_BY, createdBy, this.projectModel.currentProject, this.projectUserModel.projectOptionDefs);
        ProjectUtils.setOrUpdateProjectOption(ProjectOptionDefIds.CUSTOM_MATERIAL_NUMBER, customMaterialNumber, this.projectModel.currentProject, this.projectUserModel.projectOptionDefs);
      }
      await this.finalizeExecuteSave(configName, existingProjectId, parentGroup, createSubprojects, projectSaveInfo);
    } else {
      await this.finalizeExecuteSave(configName, existingProjectId, parentGroup, createSubprojects, projectSaveInfo);
    }
  }

  protected async finalizeExecuteSave(configName: string, existingProjectId: number, parentGroup: ProjectGroupVO,
    createSubprojects: boolean = false, projectSaveInfo: ProjectSaveInfoVO = null): Promise<void> {

    if (this._showOverwriteAlerts && createSubprojects && projectSaveInfo && ArrayUtils.isNotEmpty(projectSaveInfo.existingSubProjectNames)) {
      let text: string = '';
      projectSaveInfo.existingSubProjectNames.forEach((item) => text += '\n' + item);

      const confirmedOverwrite = await this.showAlert.confirm.overwriteSubproject(text)

      if (!confirmedOverwrite) return;

      if (existingProjectId === -1) {
        await this.executeSaveProject(existingProjectId, configName, parentGroup, SaveAsCompositeMode.NONE, null, createSubprojects);
      } else {
        this.interceptSaveProjectEvent();
        // with SMAR-68 new version will be created for project in case such project already exists
        await this.saveConfiguration(existingProjectId, this.projectModel.currentProject, createSubprojects,
          InitManager.PROJECT_OPTION_DATA_VERSION, 'asNewVersion');
      }
      return;
    }

    if (existingProjectId === -1) {
      await this.executeSaveProject(existingProjectId, configName, parentGroup, SaveAsCompositeMode.NONE, null, createSubprojects);
      return;
    }

    // with SMAR-68 new version will be created for project in case such project already exists,
    // only exception is for special user, who can edit pdm exported project
    if (SaveProjectCloseType.OVERWRITE_PROJECT === this.confirmSaveType) {
      await this.executeSaveProject(existingProjectId, configName, parentGroup, SaveAsCompositeMode.NONE, null, createSubprojects);
      return;
    }

    this.interceptSaveProjectEvent();
    this.projectModel.currentProject.projectGroup = parentGroup;
    await this.saveConfiguration(existingProjectId, this.projectModel.currentProject, createSubprojects,
      InitManager.PROJECT_OPTION_DATA_VERSION, 'asNewVersion');
  }

  private async executeSaveProject(id: number, name: string, parentGroup: ProjectGroupVO = null, saveProjectAsComplexArticle: SaveAsCompositeMode = SaveAsCompositeMode.NONE,
                                   compositeArticleConfiguration: CompositeArticleConfigurationVO = null, createSubprojects: boolean = false): Promise<void> {
    const currentProject: ProjectVO = this.projectModel.currentProject;
    currentProject.name = name;
    currentProject.projectGroup = parentGroup;
    currentProject.isPinned = this.isPinned;
    if (saveProjectAsComplexArticle) {
      // complex article gets saved:
      ProjectUtils.updateRotationInfoForExistingProject(currentProject);
      currentProject.infoProperties.setString("savedInConfiguratorVersion", this.applicationService.applicationVersion);
      this.carrierBlockManager.updateCarrierBlocksInProject(currentProject);
      let projectVO: ProjectVO = null;
      try {
        // carrier blocks probably not needed in such case
        if (SaveAsCompositeMode.BUILDING_BLOCK === saveProjectAsComplexArticle) {
          if (id !== -1) {
            currentProject.id = id;
          }
          this.representationService.update();

          projectVO = await this.saveCompositeArticleService.saveAsBuildingBlock(currentProject);
        } else {
          projectVO = await this.saveCompositeArticleService.saveCompositeArticle(compositeArticleConfiguration);
        }
        projectVO.isArticleProject = true;
        this.projectSaved(projectVO, ProjectUtils.findFirstView3DRepresentationData(this.projectModel.currentProject));
        this._savedProject$.next({success: true, projectVO: projectVO});
      } catch (error) {
        Log.error(error);
        this._savedProject$.next({success: false});
        }
    } else if (this.shouldPrepareFinalizeProject()) {
      // should no longer be used/triggered and could be removed:
      ProjectUtils.updateRotationInfoForExistingProject(currentProject);
      currentProject.infoProperties.setString("savedInConfiguratorVersion", this.applicationService.applicationVersion);
      this.carrierBlockManager.updateCarrierBlocksInProject(currentProject);
      // TODO: implement in scope of project-finalization
      // this.finalizationManager.prepareFinalizeProject(createSubprojects);
    } else {
      // we need to apply new guid in case when current project has proper id (already saved)
      // and it will be saved under a new name (new project will be created)
      if (id === -1 && !this.projectModel.isNewConfiguration) {
        currentProject.guid = StringUtils.createUuid();
      }
      this.clearExcludedPartPropertyFromProject();
      this.interceptSaveProjectEvent();
      await this.saveConfiguration(id, currentProject, createSubprojects, InitManager.PROJECT_OPTION_DATA_VERSION, 'current');
    }
  }

  public clear(): void {
    this._createSubprojects = false;
    this.newConfigName = '';
    this.saveAsCompositeMode = SaveAsCompositeMode.NONE;
    this.confirmSaveType = -1;
    this.configurationList = [];
    this.selectedProjectGroup = null;
    this.compositeArticleConfiguration = null;
    this._prepareSave = false;
  }

  public clearExcludedPartPropertyFromProject(): void {
    const articleUsages: ArticleUsageVO[] = ProjectUtils.articleUsages(this.projectModel.currentProject);
    this.clearArticleUsageInfoProperties(articleUsages, [ArticleUsageInfoPropertyDef.COMPOSITE_PART_EXCLUDED,
      ArticleUsageInfoPropertyDef.PART_OF_BUILDING_BLOCK, ArticleUsageInfoPropertyDef.PART_OF_BUILDING_BLOCK_ON_RAIL]);
  }

  private clearArticleUsageInfoProperties(articleUsages: ArticleUsageVO[], propertyNames: string[]): void {
    for (const usage of articleUsages) {
      PropertyUtils.removePropertiesByNames(usage.infoProperties, propertyNames);
    }
  }

  public shouldPrepareFinalizeProject(): boolean {
    return this.projectModel.currentProject.finalizationState === ProjectStatesDef.FINALIZATION_STATE_REQUESTED
      || this.projectModel.currentProject.finalizationState === ProjectStatesDef.FINALIZATION_STATE_ORDERED;
  }

  public async prepareSaveProject(messageType: string = SaveProjectService.PREPARE_PROJECT_SAVE_AS, saveProjectAsNew: boolean = false,
                                  showSaveAsNewProject: boolean = true): Promise<void> {
    this._prepareSave = messageType === SaveProjectService.PREPARE_PROJECT_SAVE;
    if (!this.projectModel.isCurrentProjectArticleProject && !this.projectModel.isNewConfiguration) {
      try {
        const projectSaveInfo: ProjectSaveInfoVO = await this.projectDataService.loadProjectSaveInfo(this.projectModel.currentProject.id).toPromise();
        await this.continueExecute(messageType, saveProjectAsNew, showSaveAsNewProject, projectSaveInfo);
      } catch (error) {
        Log.warn('Could not load project save info: ', error);
      }
    } else {
      await this.continueExecute(messageType, saveProjectAsNew, showSaveAsNewProject, null);
    }
  }

  private async continueExecute(messageType: string, saveProjectAsNew: boolean, showSaveAsNewProject: boolean,
    projectSaveInfo: ProjectSaveInfoVO): Promise<void> {
    if (!this.projectModel.isNewConfiguration && !this.projectModel.isCurrentProjectArticleProject
      && messageType === SaveProjectService.PREPARE_PROJECT_SAVE_AS
      && ProjectOrderingUtils.canCreateNewVersion(this.projectModel.currentProject, this.userModel.user)) {
      await this.continueWithSaveDialog(projectSaveInfo, showSaveAsNewProject);
    } else if (!this.projectModel.isNewConfiguration && !this.projectModel.isCurrentProjectArticleProject
      && messageType === SaveProjectService.PREPARE_PROJECT_SAVE && projectSaveInfo
      && ProjectOrderingUtils.canCreateNewVersion(this.projectModel.currentProject, this.userModel.user)
      && this.projectModel.currentProject.isLatestVersion) {
      // SMAR-68: Instead we now directly execute save as new version
      await this.executeSaveAsNewVersion(projectSaveInfo);
    } else if (!this.projectModel.isNewConfiguration && !this.projectModel.isCurrentProjectArticleProject
      && ProjectOrderingUtils.canCreateNewVersion(this.projectModel.currentProject, this.userModel.user)) {
      await this.continueWithSaveDialog(projectSaveInfo, showSaveAsNewProject);
    } else if (saveProjectAsNew || this.projectModel.isNewConfiguration || this.projectModel.isCurrentProjectArticleProject) {
      this.projectNavigationPresenter.openSaveProjectDialog();
    } else if (messageType === SaveProjectService.PREPARE_PROJECT_SAVE && !this.projectModel.currentProject.isLatestVersion) {
      await this.continueWithSaveDialog(projectSaveInfo, showSaveAsNewProject);
    } else {
      this.projectNavigationPresenter.openSaveProjectDialog();
    }
  }

  private async continueWithSaveDialog(projectSaveInfo: ProjectSaveInfoVO, showSaveAsNewProject: boolean): Promise<void> {
    const data = await this.projectComponentPresenter.openProjectSaveRequest(projectSaveInfo, showSaveAsNewProject, false);
    await this.confirmSaveMethodCloseHandler(data);
  }

  private async executeSaveAsNewVersion(projectSaveInfo: ProjectSaveInfoVO): Promise<void> {
    const dataCloseEvent: any = {};
    dataCloseEvent.detail = SaveProjectCloseType.SAVE_AS_NEW_VERSION;
    dataCloseEvent.projectSaveInfo = projectSaveInfo;
    await this.confirmSaveMethodCloseHandler(dataCloseEvent);
  }

  private interceptSaveProjectEvent(): void {
    ProjectUtils.updateRotationInfoForExistingProject(this.projectModel.currentProject);
    this.projectModel.currentInfoProperties.setString("savedInConfiguratorVersion", this.applicationService.applicationVersion);
    this.carrierBlockManager.updateCarrierBlocksInProject(this.projectModel.currentProject);
  }

  public async saveConfiguration(
    objectId: number,
    project: ProjectVO,
    createSubprojects: boolean,
    projectOptionDataVersion: string,
    type: SaveType,
    updateAllFields: boolean = false,
    errorHandlingRequired: boolean = true,
  ): Promise<boolean> {


    if (project !== this.projectModel.currentProject) {
      throw new Error('Saving of another project, which is different from currentProjectGroup, is currently not supported!');
    }

    if (projectOptionDataVersion) {
      this.projectModel.currentInfoProperties.setString("projectOptionDataVersion", projectOptionDataVersion);
    }
    this.representationService.update();
    const representationData = ProjectUtils.findFirstView3DRepresentationData(this.projectModel.currentProject) as OBJRepresentationDataVO;
    try {
      let projectVO: ProjectVO;
      switch (type) {
        case 'current': {
          projectVO = await lastValueFrom(this.projectDataService.saveProject(objectId, project, representationData, createSubprojects, updateAllFields, errorHandlingRequired));
          break;
        }
        case 'keepModificationDate': {
          projectVO = await lastValueFrom(this.projectDataService.saveProjectKeepingModificationDate(objectId, project, representationData, errorHandlingRequired));
          break;
        }
        case 'asNewVersion': {
          projectVO = await lastValueFrom(this.projectDataService.saveProjectAsNewVersion(objectId, project, representationData, createSubprojects, false, errorHandlingRequired));
          break;
        }
        case 'overwriteVersion': {
          const versionToOverwrite = await this.projectComponentPresenter.openOverwriteProjectVersion();
          if (versionToOverwrite === undefined) {
            return;
          }
          projectVO = await lastValueFrom(this.projectDataService.overwriteProjectVersion(project.id, this.projectModel.currentProject, versionToOverwrite, representationData));
          break;
        }
        default: {
          assertExhaustiveSwitch(type);
        }
      }
      this.projectSaved(projectVO, representationData);
      return true;
    } catch (error) {
      Log.error('Could not save project: ', error)
      await this.saveFailure();
      return false;
    }

  }

  private projectSaved(projectVO: ProjectVO, representationData: RepresentationDataVO): void {
    this.projectService.updateProjectDataLoaded(projectVO, representationData);
    this.userSettings.saveLastSelectedProjectFromProjectVO(projectVO, false);
    this.projectModel.resetProjectChanged();
    this.projectRequestProcessorDelegator.saveProjectSucceeded();
    this._savedProject$.next({ success: true, projectVO: projectVO });
    if (this._prepareSave && this.projectModel.currentProject && this.projectModel.currentProject.projectGroup) {
      this.toastrService.success(
          this.localeResourceManager.getValueByKey('info_message.project_saved.body'),
          this.localeResourceManager.getValueByKey('info_message.project_saved.title', { 0: this.projectModel.currentProject.projectGroup.groupName })
      );
    }
    this.clear();
  }

  private async saveFailure(): Promise<void> {
    this.clear();
    this.carrierBlockManager.restoreEmptyProjectCarrierBlocks();
    this._savedProject$.next({ success: false });
  }

  public async saveProjectBeforeAction(message: string): Promise<boolean> {
    if (this.projectModel.currentProjectRequiresSaving) {
      const confirmedSave = await this.showAlert.confirm.save(message);
      if (confirmedSave) {
        await this.projectNavigationPresenter.openSaveProjectDialog();
      }
    }
    return !this.projectModel.currentProjectRequiresSaving;
  }

  public async ensureProjectSaved(): Promise<boolean> {
    if (UserUtils.hasEditProjectPermission(this.projectModel.user) && this.projectModel.currentProjectRequiresSaving) {
      const saved = await this.projectComponentPresenter.openSaveProjectPopUp();
      return saved;
    }
    return true;
  }

  private get showAlert() {
    return {
      error: {
        readOnly: () => this.alertHelper.showError('save.title.cannot_override_read_only_project', 'save.message.cannot_override_read_only_project'),
        writeProtected: () => this.alertHelper.showError('save.title.cannot_override_write_protected_configuration',
          'save.message.cannot_override_write_protected_configuration'),
        versionLocked: () => this.alertHelper.showError('save.title.cannot_override_version_locked_configuration',
          'save.message.cannot_override_version_locked_configuration'),
        buildingBlockNameExists: (name: string) => this.alertHelper.showError('save.title.error_building_block_name_exists',
          'save.message.error_building_block_name_exists', {translationParams: [name]}),
        changesDoNotEffectFinalizedProjectVersion: (projectVersionDisplayStringWithDate: string) => this.alertHelper.showError('save.title.saved_changes_do_not_effect_finalized_project_version',
          'save.message.saved_changes_do_not_effect_finalized_project_version', {translationParams: [projectVersionDisplayStringWithDate]}),
        cannotOverwriteProtectedSubproject: (text: string) => this.alertHelper.showError('save.title.cannot_override_write_protected_subproject',
          'save.message.cannot_override_write_protected_subproject', {translationParams: [text]}),
      },
      confirm: {
        overwrite: async (configName: string) => await this.alertHelper.showAlert(
          'save.title.confirm_override_configuration',
          'save.message.confirm_override_configuration',
          { 0: escape(configName) },
          [AlertButton.YES, AlertButton.NO],
          "error"
        ) == AlertButton.YES,
        overwriteSubproject: async (text: string) => await this.alertHelper.showAlert('save.title.confirm_override_subproject', 'save.message.confirm_override_subproject', {0: escape(text)},
          [AlertButton.YES, AlertButton.NO], 'error') == AlertButton.YES,
        save: async (message: string) => await this.alertHelper.showAlert('save.title', message, null, [AlertButton.OK, AlertButton.CANCEL], "error") == AlertButton.OK,
      },
      info: {
        changesDoNotEffectFinalizedProjectVersion: (projectVersionDisplayStringWithDate: string) => this.alertHelper.showMessage('save.title.saved_changes_do_not_effect_finalized_project_version',
          'save.message.saved_changes_do_not_effect_finalized_project_version', {translationParams: [projectVersionDisplayStringWithDate]}),
      }
    }
  }
}
